import { createSlice, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';

import { rootReducer } from '../../../lib/store';
import type { Span } from '../../workspace-common/types/responseTypes';
import type { HighlightedTextNew, SelectedSpan } from '../../workspace-common/types/Text';

export interface AnnotationState {
  filterBy: string;
  noteSortBy: string;
  groupBy: string;
  selectedNoteId: string | null;
  areAllConceptsExpanded: boolean | undefined;
  expandedConceptIds: string[];
  conceptAnnotationIdsInEditMode: string[];
  addEvidenceModeForConceptId: string | null;
  highlightedTextSpans: HighlightedTextNew[];
  // Pending spans: spans added but not yet saved to server, keyed by annotationId
  pendingSpans: Record<string, Span[]>;
  viewOptionExpanded: boolean;
  deleteSpanError: Record<string, string | null>;
  createAnnotationError: Record<string, string | null>;
  editAnnotationError: Record<string, string | null>;
  deleteAnnotationError: Record<string, string | null>;
  dirtyAnnotationKeys: string[];
  taskId: string;
  annotationStarted: boolean;
  evidenceIdsToDelete: Record<string, string[]>; //Annotation id as key
  scrollToHighlightTrigger: number;
}

const initialState: AnnotationState = {
  filterBy: 'all',
  noteSortBy: 'ascending',
  groupBy: 'none',
  selectedNoteId: null,
  areAllConceptsExpanded: undefined,
  expandedConceptIds: [],
  conceptAnnotationIdsInEditMode: [],
  addEvidenceModeForConceptId: null,
  highlightedTextSpans: [],
  pendingSpans: {},
  viewOptionExpanded: false,
  deleteSpanError: {},
  createAnnotationError: {},
  editAnnotationError: {},
  deleteAnnotationError: {},
  dirtyAnnotationKeys: [],
  taskId: '',
  annotationStarted: false,
  evidenceIdsToDelete: {},
  scrollToHighlightTrigger: 0,
};

export const annotationSlice = createSlice({
  name: 'annotation',
  initialState,
  reducers: {
    setFilterBy: (state, action: PayloadAction<string>) => {
      state.filterBy = action.payload;
    },
    setNoteSortBy: (state, action: PayloadAction<string>) => {
      state.noteSortBy = action.payload;
    },
    clearNoteSortBy: (state) => {
      state.noteSortBy = 'ascending';
    },
    clearFilterBy: (state) => {
      state.filterBy = 'all';
    },
    setGroupBy: (state, action: PayloadAction<string>) => {
      state.groupBy = action.payload;
    },
    clearGroupBy: (state) => {
      state.groupBy = 'none';
    },
    setSelectedNoteId: (state, action: PayloadAction<string | null>) => {
      state.selectedNoteId = action.payload;
    },
    clearSelectedNoteId: (state) => {
      state.selectedNoteId = null;
    },
    setAreAllConceptsExpanded: (state, action: PayloadAction<boolean>) => {
      state.areAllConceptsExpanded = action.payload;
    },
    clearAreAllConceptsExpanded: (state) => {
      state.areAllConceptsExpanded = undefined;
    },
    addToExpandedConceptIds: (state, action: PayloadAction<string>) => {
      if (state.expandedConceptIds.includes(action.payload)) {
        return;
      }
      state.expandedConceptIds.push(action.payload);
    },
    removeFromExpandedConceptIds: (state, action: PayloadAction<string>) => {
      state.deleteSpanError = {};
      if (!state.expandedConceptIds.includes(action.payload)) {
        return;
      }
      state.expandedConceptIds = state.expandedConceptIds.filter((id) => id !== action.payload);
    },
    addToConceptAnnotationIdsInEditMode: (state, action: PayloadAction<string>) => {
      if (state.conceptAnnotationIdsInEditMode.includes(action.payload)) {
        return;
      }
      state.conceptAnnotationIdsInEditMode.push(action.payload);
    },
    removeFromConceptAnnotationIdsInEditMode: (state, action: PayloadAction<string>) => {
      if (!state.conceptAnnotationIdsInEditMode.includes(action.payload)) {
        return;
      }
      state.conceptAnnotationIdsInEditMode = state.conceptAnnotationIdsInEditMode.filter(
        (id) => id !== action.payload
      );
    },
    clearConceptAnnotationIdsInEditMode: (state) => {
      state.conceptAnnotationIdsInEditMode = [];
    },
    setAddEvidenceModeForConceptId: (state, action: PayloadAction<string>) => {
      state.addEvidenceModeForConceptId = action.payload;
    },
    clearAddEvidenceModeForConceptId: (state) => {
      state.addEvidenceModeForConceptId = null;
    },
    setHighlightedTextSpans: (state, action: PayloadAction<HighlightedTextNew[]>) => {
      state.highlightedTextSpans = action.payload;
    },
    addHighlightedTextSpan: (state, action: PayloadAction<SelectedSpan & { number?: number }>) => {
      if (
        state.highlightedTextSpans.some(
          (ht) =>
            ht.offsetStart === action.payload.startOffset &&
            ht.offsetEnd === action.payload.endOffset &&
            ht.noteId === action.payload.noteId &&
            ht.annotationId === action.payload.annotationId
        )
      ) {
        return;
      }
      // Calculate the number based on existing spans for this annotation
      // If number is provided, use it; otherwise calculate it
      let number = action.payload.number;
      if (number === undefined) {
        // Count existing spans for this annotation
        const existingSpansForAnnotation = state.highlightedTextSpans.filter(
          (ht) => ht.annotationId === action.payload.annotationId
        );
        // Count pending spans for this annotation
        const pendingSpansForAnnotation = state.pendingSpans[action.payload.annotationId] || [];
        // Total count = existing highlighted spans + pending spans + 1 (for this new one)
        number = existingSpansForAnnotation.length + pendingSpansForAnnotation.length + 1;
      }
      state.highlightedTextSpans.push({
        number,
        offsetStart: action.payload.startOffset,
        offsetEnd: action.payload.endOffset,
        noteId: action.payload.noteId,
        annotationId: action.payload.annotationId,
        text: action.payload.text,
      });
    },
    // Add a single span to highlightedTextSpans (for "Show Evidence" button)
    addHighlightedTextSpanFromSpan: (
      state,
      action: PayloadAction<{ span: Span; number: number; annotationId: string }>
    ) => {
      const { span, number, annotationId } = action.payload;
      if (
        state.highlightedTextSpans.some(
          (ht) =>
            ht.offsetStart === span.offsetStart &&
            ht.offsetEnd === span.offsetEnd &&
            ht.noteId === span.noteId &&
            ht.annotationId === annotationId
        )
      ) {
        return;
      }
      state.highlightedTextSpans.push({
        number,
        offsetStart: span.offsetStart,
        offsetEnd: span.offsetEnd,
        noteId: span.noteId,
        annotationId,
        text: span.text,
      });
    },
    removeHighlightedTextSpan: (
      state,
      action: PayloadAction<{
        offsetStart: number;
        offsetEnd: number;
        noteId: string;
        annotationId: string;
      }>
    ) => {
      state.highlightedTextSpans = state.highlightedTextSpans.filter(
        (ht) =>
          !(
            ht.offsetStart === action.payload.offsetStart &&
            ht.offsetEnd === action.payload.offsetEnd &&
            ht.noteId === action.payload.noteId &&
            ht.annotationId === action.payload.annotationId
          )
      );
      // Renumber remaining spans for this annotation in insertion order
      state.highlightedTextSpans
        .filter((ht) => ht.annotationId === action.payload.annotationId)
        .forEach((ht, i) => {
          ht.number = i + 1;
        });
    },
    clearHighlightedTextSpans: (state) => {
      state.highlightedTextSpans = [];
    },
    // Pending spans management
    addPendingSpan: (state, action: PayloadAction<{ annotationId: string; span: Span }>) => {
      const { annotationId, span } = action.payload;
      if (!state.pendingSpans[annotationId]) {
        state.pendingSpans[annotationId] = [];
      }
      // Check if span already exists (same noteId, offsetStart, offsetEnd)
      const exists = state.pendingSpans[annotationId].some(
        (s) =>
          s.noteId === span.noteId &&
          s.offsetStart === span.offsetStart &&
          s.offsetEnd === span.offsetEnd
      );
      if (!exists) {
        state.pendingSpans[annotationId].push(span);
      }
    },
    removePendingSpan: (
      state,
      action: PayloadAction<{
        annotationId: string;
        noteId: string;
        offsetStart: number;
        offsetEnd: number;
      }>
    ) => {
      const { annotationId, noteId, offsetStart, offsetEnd } = action.payload;
      if (state.pendingSpans[annotationId]) {
        state.pendingSpans[annotationId] = state.pendingSpans[annotationId].filter(
          (s) =>
            !(s.noteId === noteId && s.offsetStart === offsetStart && s.offsetEnd === offsetEnd)
        );
        // Clean up empty arrays
        if (state.pendingSpans[annotationId].length === 0) {
          delete state.pendingSpans[annotationId];
        }
      }
    },
    clearPendingSpansForAnnotation: (state, action: PayloadAction<string>) => {
      delete state.pendingSpans[action.payload];
    },
    setViewOptionExpanded: (state, action: PayloadAction<boolean>) => {
      state.viewOptionExpanded = action.payload;
    },
    clearViewOptionExpanded: (state) => {
      state.viewOptionExpanded = false;
    },
    setDeleteSpanError: (
      state,
      action: PayloadAction<{ annotationId: string; error: string | null }>
    ) => {
      state.deleteSpanError[action.payload.annotationId] = action.payload.error;
    },
    clearDeleteSpanError: (state) => {
      state.deleteSpanError = {};
    },
    setCreateAnnotationError: (
      state,
      action: PayloadAction<{ conceptId: string; error: string | null }>
    ) => {
      state.createAnnotationError[action.payload.conceptId] = action.payload.error;
    },
    clearCreateAnnotationError: (state) => {
      state.createAnnotationError = {};
    },
    setEditAnnotationError: (
      state,
      action: PayloadAction<{ annotationId: string; error: string | null }>
    ) => {
      state.editAnnotationError[action.payload.annotationId] = action.payload.error;
    },
    clearEditAnnotationError: (state) => {
      state.editAnnotationError = {};
    },
    setDeleteAnnotationError: (
      state,
      action: PayloadAction<{ annotationId: string; error: string | null }>
    ) => {
      state.deleteAnnotationError[action.payload.annotationId] = action.payload.error;
    },
    clearDeleteAnnotationError: (state) => {
      state.deleteAnnotationError = {};
    },
    addToDirtyAnnotationKeys: (state, action: PayloadAction<string>) => {
      if (!state.dirtyAnnotationKeys.includes(action.payload)) {
        state.dirtyAnnotationKeys.push(action.payload);
      }
    },
    removeFromDirtyAnnotationKeys: (state, action: PayloadAction<string>) => {
      state.dirtyAnnotationKeys = state.dirtyAnnotationKeys.filter((key) => key !== action.payload);
    },
    setTaskId: (state, action: PayloadAction<string>) => {
      state.taskId = action.payload;
    },
    clearTaskId: (state) => {
      state.taskId = '';
    },
    setAnnotationStarted: (state) => {
      state.annotationStarted = true;
    },
    clearAnnotationStarted: (state) => {
      state.annotationStarted = false;
    },
    triggerScrollToHighlight: (state) => {
      state.scrollToHighlightTrigger += 1;
    },
    addToEvidenceIdsToDelete: (
      state,
      action: PayloadAction<{ annotationId: string; evidenceId: string }>
    ) => {
      const { annotationId, evidenceId } = action.payload;
      if (state.evidenceIdsToDelete[annotationId]) {
        state.evidenceIdsToDelete[annotationId].push(evidenceId);
      } else {
        state.evidenceIdsToDelete[annotationId] = [evidenceId];
      }
    },
    clearEvidenceIdsToDelete: (
      state,
      action: PayloadAction<{ annotationId?: string } | undefined>
    ) => {
      const annotationId = action.payload?.annotationId;

      if (annotationId) {
        delete state.evidenceIdsToDelete[annotationId];
      } else {
        state.evidenceIdsToDelete = {};
      }
    },
    resetAnnotationState: (state) => {
      // Reset all state to initial values
      state.filterBy = initialState.filterBy;
      state.noteSortBy = initialState.noteSortBy;
      state.groupBy = initialState.groupBy;
      state.selectedNoteId = initialState.selectedNoteId;
      state.areAllConceptsExpanded = initialState.areAllConceptsExpanded;
      state.expandedConceptIds = initialState.expandedConceptIds;
      state.conceptAnnotationIdsInEditMode = initialState.conceptAnnotationIdsInEditMode;
      state.addEvidenceModeForConceptId = initialState.addEvidenceModeForConceptId;
      state.highlightedTextSpans = initialState.highlightedTextSpans;
      state.pendingSpans = initialState.pendingSpans;
      state.viewOptionExpanded = initialState.viewOptionExpanded;
      state.deleteSpanError = initialState.deleteSpanError;
      state.createAnnotationError = initialState.createAnnotationError;
      state.editAnnotationError = initialState.editAnnotationError;
      state.deleteAnnotationError = initialState.deleteAnnotationError;
      state.taskId = initialState.taskId;
      state.editAnnotationError = initialState.editAnnotationError;
      state.evidenceIdsToDelete = initialState.evidenceIdsToDelete;
      state.dirtyAnnotationKeys = initialState.dirtyAnnotationKeys;
      state.scrollToHighlightTrigger = initialState.scrollToHighlightTrigger;
    },
  },
});

export const {
  setFilterBy,
  clearFilterBy,
  setNoteSortBy,
  clearNoteSortBy,
  setGroupBy,
  clearGroupBy,
  setSelectedNoteId,
  clearSelectedNoteId,
  setAreAllConceptsExpanded,
  clearAreAllConceptsExpanded,
  addToExpandedConceptIds,
  removeFromExpandedConceptIds,
  addToConceptAnnotationIdsInEditMode,
  removeFromConceptAnnotationIdsInEditMode,
  clearConceptAnnotationIdsInEditMode,
  setAddEvidenceModeForConceptId,
  clearAddEvidenceModeForConceptId,
  setHighlightedTextSpans,
  clearHighlightedTextSpans,
  addHighlightedTextSpan,
  addHighlightedTextSpanFromSpan,
  removeHighlightedTextSpan,
  addPendingSpan,
  removePendingSpan,
  clearPendingSpansForAnnotation,
  setViewOptionExpanded,
  clearViewOptionExpanded,
  setDeleteSpanError,
  clearDeleteSpanError,
  setCreateAnnotationError,
  clearCreateAnnotationError,
  setEditAnnotationError,
  clearEditAnnotationError,
  setDeleteAnnotationError,
  clearDeleteAnnotationError,
  setTaskId,
  clearTaskId,
  setAnnotationStarted,
  clearAnnotationStarted,
  resetAnnotationState,
  addToEvidenceIdsToDelete,
  clearEvidenceIdsToDelete,
  triggerScrollToHighlight,
  addToDirtyAnnotationKeys,
  removeFromDirtyAnnotationKeys,
} = annotationSlice.actions;

declare module '../../../lib/store' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof annotationSlice> {}
}

annotationSlice.injectInto(rootReducer);
